/**
 * MultiPropertyPanel — editor surface for multi-property templates
 * (förhandlingsordning, förhandlingsöverenskommelse). Per ADR-0025.
 *
 * Renders an ordered list of linked properties as cards. Each card
 * shows the fastighetsbeteckning + composed address, plus a "Ta bort"
 * action. Below the list: a search-combobox for existing properties,
 * plus an inline "Skapa ny fastighet" form (same shape as
 * `PropertyAutofillGroup`). Soft-duplicate warnings surface on create.
 *
 * Reorder is move-up/move-down arrows on each card (no DnD library
 * dependency for now — keystroke/click is fine for small lists).
 *
 * The rendered cells on the contract are aggregated strings
 * (`property_list` autofill), recomputed server-side from the linked
 * properties. Each can be switched to manual entry (free text that
 * property changes leave alone) via `PropertyListFields`, passed in as
 * `children`.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { Building2, X, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import {
  tenantApi,
  type LinkedProperty,
  type PropertySearchHit,
  type SoftDupProperty,
} from '../api.js';
import { EntityCombobox, type EntityComboboxOption } from './EntityCombobox.js';

export function MultiPropertyPanel({
  token,
  contractId,
  linkedProperties,
  defaultGroupId,
  required,
  label,
  help,
  onChanged,
  disabled,
  children,
}: {
  token: string;
  contractId: string;
  linkedProperties: ReadonlyArray<LinkedProperty>;
  defaultGroupId: string | null;
  required?: boolean;
  /** Heading label, e.g. "Fastigheter". */
  label: string;
  help?: string;
  /** Callback fired after a successful add/remove/reorder. Editor uses
   *  it to invalidate `contracts.get`. */
  onChanged: () => Promise<void> | void;
  disabled: boolean;
  /** Rendered at the bottom of the panel (the aggregated text fields). */
  children?: ReactNode;
}) {
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftStreet, setDraftStreet] = useState('');
  const [draftPostal, setDraftPostal] = useState('');
  const [draftOrt, setDraftOrt] = useState('');
  const [draftKommun, setDraftKommun] = useState('');
  const [busy, setBusy] = useState(false);
  const [softDups, setSoftDups] = useState<SoftDupProperty[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const search = useCallback(
    async (q: string): Promise<EntityComboboxOption<PropertySearchHit>[]> => {
      const r = await tenantApi.properties.search({
        q: q.trim() || undefined,
      });
      // Exclude already-linked properties from the picker — re-linking
      // is a no-op server-side, but showing them in the dropdown looks
      // broken.
      const linkedIds = new Set(linkedProperties.map((p) => p.id));
      return r.properties
        .filter((p) => !linkedIds.has(p.id))
        .map((p) => ({
          id: p.id,
          primary: p.name,
          secondary:
            [
              p.street,
              [p.postalCode, p.ort].filter(Boolean).join(' ').trim(),
            ]
              .filter(Boolean)
              .join(' · ') || null,
          value: p,
        }));
    },
    [token, linkedProperties],
  );

  const addLink = async (propertyId: string) => {
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.contract_properties.add({
        contract_id: contractId,
        property_id: propertyId,
      });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const removeLink = async (propertyId: string) => {
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.contract_properties.remove({
        contract_id: contractId,
        property_id: propertyId,
      });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const reorderTo = async (next: ReadonlyArray<LinkedProperty>) => {
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.contract_properties.reorder({
        contract_id: contractId,
        property_ids: next.map((p) => p.id),
      });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...linkedProperties];
    [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
    void reorderTo(next);
  };
  const moveDown = (index: number) => {
    if (index >= linkedProperties.length - 1) return;
    const next = [...linkedProperties];
    [next[index + 1], next[index]] = [next[index]!, next[index + 1]!];
    void reorderTo(next);
  };

  const submitCreate = async () => {
    if (!defaultGroupId) {
      setErr('Ingen grupp att skapa i.');
      return;
    }
    const name = draftName.trim();
    if (!name) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await tenantApi.properties.create({
        group_id: defaultGroupId,
        name,
        street: draftStreet.trim() || null,
        postal_code: draftPostal.trim() || null,
        ort: draftOrt.trim() || null,
        kommun: draftKommun.trim() || null,
      });
      if (r.warnings?.soft_duplicates?.length) {
        setSoftDups(r.warnings.soft_duplicates);
      }
      await tenantApi.contract_properties.add({
        contract_id: contractId,
        property_id: r.property.id,
      });
      await onChanged();
      setCreating(false);
      setDraftName('');
      setDraftStreet('');
      setDraftPostal('');
      setDraftOrt('');
      setDraftKommun('');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const linkInstead = async (candidateId: string) => {
    await addLink(candidateId);
    setSoftDups(null);
  };

  return (
    <fieldset className="flex flex-col gap-3 rounded border border-stone-200 p-3">
      <legend className="flex items-center gap-2 px-1 text-xs font-medium uppercase tracking-wider text-stone-500">
        <Building2 className="h-3.5 w-3.5" />
        {label}
        {required && (
          <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[9px] normal-case tracking-normal text-stone-500">
            Obligatorisk
          </span>
        )}
      </legend>

      {help && <p className="px-1 text-[11px] text-stone-500">{help}</p>}

      {err && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
          {err}
        </div>
      )}

      {softDups && softDups.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium">Möjligt duplikat</div>
              <div className="mt-1 text-amber-800">
                Liknande fastigheter finns redan. Vill du länka till någon
                av dessa istället?
              </div>
              <ul className="mt-2 space-y-1">
                {softDups.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <div className="truncate">
                      <span className="font-medium">{c.name}</span>
                      {c.street && (
                        <span className="ml-1 text-amber-700">· {c.street}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => void linkInstead(c.id)}
                      className="shrink-0 rounded bg-amber-200 px-2 py-0.5 text-amber-900 hover:bg-amber-300"
                    >
                      Länka istället
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setSoftDups(null)}
                className="mt-2 text-amber-700 hover:underline"
              >
                Behåll den nya
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Linked-property cards */}
      {linkedProperties.length > 0 && (
        <ul className="flex flex-col gap-2">
          {linkedProperties.map((p, i) => (
            <li
              key={p.id}
              className="flex items-start gap-2 rounded border border-stone-200 bg-stone-50 px-3 py-2 text-xs"
            >
              <div className="flex flex-1 flex-col gap-0.5">
                <span className="font-medium text-stone-800">{p.name}</span>
                {p.address && (
                  <span className="whitespace-pre-line text-stone-600">
                    {p.address}
                  </span>
                )}
              </div>
              {!disabled && (
                <div className="flex shrink-0 flex-col items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => moveUp(i)}
                    disabled={i === 0 || busy}
                    className="rounded p-0.5 text-stone-500 hover:bg-stone-200 disabled:opacity-30"
                    title="Flytta upp"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(i)}
                    disabled={i >= linkedProperties.length - 1 || busy}
                    className="rounded p-0.5 text-stone-500 hover:bg-stone-200 disabled:opacity-30"
                    title="Flytta ned"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => void removeLink(p.id)}
                  disabled={busy}
                  className="shrink-0 rounded p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
                  title="Ta bort"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add-link surface */}
      {!disabled &&
        (creating ? (
          <div className="space-y-2 rounded border border-stone-200 bg-stone-50 p-3">
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Beteckning (t.ex. Klockaren 5)"
              className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
            />
            <input
              value={draftStreet}
              onChange={(e) => setDraftStreet(e.target.value)}
              placeholder="Adress, t.ex. Götgatan 35"
              className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
            />
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <input
                value={draftPostal}
                onChange={(e) => setDraftPostal(e.target.value)}
                placeholder="116 21"
                className="rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
              />
              <input
                value={draftOrt}
                onChange={(e) => setDraftOrt(e.target.value)}
                placeholder="Stockholm"
                className="rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
              />
            </div>
            <input
              value={draftKommun}
              onChange={(e) => setDraftKommun(e.target.value)}
              placeholder="Kommun, t.ex. Stockholm"
              className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setDraftName('');
                  setDraftStreet('');
                  setDraftPostal('');
                  setDraftOrt('');
                  setDraftKommun('');
                }}
                className="rounded px-3 py-1 text-xs font-medium text-stone-600"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={() => void submitCreate()}
                disabled={busy || draftName.trim().length === 0 || !defaultGroupId}
                className="rounded bg-stone-900 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
              >
                {busy ? 'Skapar…' : 'Skapa & länka'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <EntityCombobox
              placeholder="Sök fastighet att lägga till…"
              onSearch={search}
              onPick={(opt) => void addLink(opt.id)}
              emptyHint="Inga fler fastigheter att lägga till."
            />
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="text-xs text-stone-600 hover:underline"
            >
              + Skapa ny fastighet
            </button>
          </div>
        ))}
      {children}
    </fieldset>
  );
}
